import { Component, type ErrorInfo, type ReactNode } from "react";

// App-wide safety net: a single render-time throw anywhere below this boundary
// shows a recoverable error card instead of unmounting the whole tree to a
// blank white screen. Class component because error boundaries have no hook
// equivalent (getDerivedStateFromError / componentDidCatch).
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface it in the console for support/debugging; the UI stays usable.
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="page">
          <div className="banner-error">
            <p>
              <strong>Something went wrong rendering this page.</strong>
            </p>
            <p className="muted">{this.state.error.message}</p>
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <a href="/app/crm" className="btn btn-ghost" onClick={this.reset}>
                Back to investors
              </a>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => window.location.reload()}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
